import React, { useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeWindow {
  start: string; // "HH:MM"
  end: string;   // "HH:MM"
}

export type NotificationSchedule = Record<string, TimeWindow[]> | null;

interface Props {
  value: NotificationSchedule;
  onChange: (v: NotificationSchedule) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS = [
  { key: 'monday',    short: 'Mon', label: 'Monday'    },
  { key: 'tuesday',   short: 'Tue', label: 'Tuesday'   },
  { key: 'wednesday', short: 'Wed', label: 'Wednesday' },
  { key: 'thursday',  short: 'Thu', label: 'Thursday'  },
  { key: 'friday',    short: 'Fri', label: 'Friday'    },
  { key: 'saturday',  short: 'Sat', label: 'Saturday'  },
  { key: 'sunday',    short: 'Sun', label: 'Sunday'    },
];

const DEFAULT_WINDOW: TimeWindow = { start: '08:00', end: '17:00' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(t: string): string {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const TimeInput = ({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) => (
  <div className="nss-time-field">
    <span className="nss-time-label">{label}</span>
    <input
      type="time"
      className="ls-input nss-time-input"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

const WindowRow = ({
  window,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  window: TimeWindow;
  index: number;
  canRemove: boolean;
  onChange: (w: TimeWindow) => void;
  onRemove: () => void;
}) => (
  <div className="nss-window-row">
    <TimeInput
      label="From"
      value={window.start}
      onChange={v => onChange({ ...window, start: v })}
    />
    <span className="nss-window-arrow">→</span>
    <TimeInput
      label="Until"
      value={window.end}
      onChange={v => onChange({ ...window, end: v })}
    />
    {canRemove && (
      <button
        type="button"
        className="nss-remove-btn"
        onClick={onRemove}
        title="Remove window"
      >
        ×
      </button>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const NotificationScheduleSelector = ({ value, onChange }: Props) => {
  const anytime = value === null;
  const schedule = value ?? {};

  // Which days are active (have entries in the schedule object)
  const activeDays = new Set(Object.keys(schedule));

  const toggleAnytime = () => {
    onChange(anytime ? {} : null);
  };

  const toggleDay = (key: string) => {
    if (anytime) return; // shouldn't happen but guard
    const next = { ...schedule };
    if (activeDays.has(key)) {
      delete next[key];
    } else {
      next[key] = [{ ...DEFAULT_WINDOW }];
    }
    onChange(next);
  };

  const updateWindow = (day: string, index: number, win: TimeWindow) => {
    const next = { ...schedule };
    next[day] = next[day].map((w, i) => (i === index ? win : w));
    onChange(next);
  };

  const addWindow = (day: string) => {
    const next = { ...schedule };
    next[day] = [...(next[day] || []), { ...DEFAULT_WINDOW }];
    onChange(next);
  };

  const removeWindow = (day: string, index: number) => {
    const next = { ...schedule };
    next[day] = next[day].filter((_, i) => i !== index);
    if (next[day].length === 0) delete next[day];
    onChange(next);
  };

  // Summary text shown in the anytime toggle area
  const scheduleSummary = (): string => {
    if (anytime || Object.keys(schedule).length === 0) return '';
    const parts = DAYS
      .filter(d => activeDays.has(d.key))
      .map(d => d.short);
    return parts.join(', ');
  };

  return (
    <div className="nss-wrapper ls-field-last">
      <label className="ls-label">Notification Schedule</label>

      {/* ── Anytime toggle ─────────────────────────────────────── */}
      <label className={`nss-anytime-row ${anytime ? 'nss-anytime-active' : ''}`}>
        <div className={`ls-checkbox-box ${anytime ? 'ls-checked' : ''}`}>
          {anytime && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <input
          type="checkbox"
          className="ls-checkbox-hidden"
          checked={anytime}
          onChange={toggleAnytime}
        />
        <div className="nss-anytime-text">
          <span className="nss-anytime-title">Notify me anytime</span>
          <span className="nss-anytime-desc">
            {anytime
              ? 'You\'ll receive alerts as soon as they happen, any day or time.'
              : scheduleSummary()
                ? `Active days: ${scheduleSummary()}`
                : 'Choose specific days and time windows below. Enable this option if you want to receive alerts 24/7 and do not need a custom notification schedule.'}
          </span>
        </div>
      </label>

      {/* ── Day grid (only when not anytime) ───────────────────── */}
      {!anytime && (
        <div className="nss-body">
          {/* Day pill row */}
          <div className="nss-day-pills">
            {DAYS.map(d => {
              const active = activeDays.has(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  className={`nss-day-pill ${active ? 'nss-day-pill-active' : ''}`}
                  onClick={() => toggleDay(d.key)}
                >
                  {d.short}
                </button>
              );
            })}
          </div>

          {/* Per-day time windows */}
          {Object.keys(schedule).length === 0 && (
            <p className="nss-empty-hint">
              Select at least one day to set a time window.
            </p>
          )}

          {DAYS.filter(d => activeDays.has(d.key)).map(d => (
            <div key={d.key} className="nss-day-block">
              <div className="nss-day-block-header">
                <span className="nss-day-block-name">{d.label}</span>
                <button
                  type="button"
                  className="nss-add-window-btn"
                  onClick={() => addWindow(d.key)}
                  disabled={(schedule[d.key]?.length ?? 0) >= 3}
                  title="Add time window"
                >
                  + Add window
                </button>
              </div>

              {(schedule[d.key] || []).map((win, idx) => (
                <WindowRow
                  key={idx}
                  window={win}
                  index={idx}
                  canRemove={(schedule[d.key]?.length ?? 0) > 1}
                  onChange={w => updateWindow(d.key, idx, w)}
                  onRemove={() => removeWindow(d.key, idx)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};